import React from "react";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import Tooltip from "@mui/material/Tooltip";
import Pill from "../../shared/helpers/Pill";
import { formatDateReadable, formatTimestampDateReadable } from "../../utilities/helpers";
import { getInitials } from "../../utilities/helpers";
import IconButton from "@mui/material/IconButton";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import AvatarSmall from "../../shared/helpers/AvatarSmall";
import TimeOffApprovedCalendarModal from "./TimeOffApprovedCalendarModal";
import TimeOffApprovedDrawer from "./TimeOffApprovedDrawer";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { get_t_str } from "../../../../../helpers/helpers";

export default function TimeOffApprovedTable(props) {

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // New state for drawer
  const [isDrawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedRequest, setSelectedRequest] = React.useState(null);

  const timeOffHistory = props.timeOffHistory?.sort((a, b) =>
    b.acting_manager_response_at?.localeCompare(a.acting_manager_response_at)
  );

  const handleRowClick = (timeOff) => {
    // Format the request info to match what the drawer expects
    const requestInfo = {
      firstName: timeOff.store_team_member.first_name,
      lastName: timeOff.store_team_member.last_name,
      title: timeOff.title,
      time_off_type: timeOff.time_off_type,
      startDate: timeOff.date_lower_bound,
      endDate: timeOff.date_upper_bound,
      startTime: timeOff.start_qhnum,
      endTime: timeOff.end_qhnum,
      note: timeOff.note,
      managerNote: timeOff.acting_manager_note,
      dateSubmitted: timeOff.created_at,
      dateResponded: timeOff.acting_manager_response_at,
      actedBy: timeOff.acting_manager_name,
      status: timeOff.status
    };
    setSelectedRequest(requestInfo);
    setDrawerOpen(true);
  };

  return (
    <>
      <div className="mt-12">
        <div>
          <div className="flex items-center px-6">
            <div className="flex gap-x-4">
              <h1 className="text-xl font-bold text-slate-900">History</h1>
              <div
                className="px-5 py-2 bg-violet-50 hover:bg-violet-100 text-violet-700 text-xs rounded-lg h-fit cursor-pointer transition-colors"
                onClick={handleOpen}
              >
                <p>Time Off Calendar</p>
              </div>
            </div>
          </div>
          <div className="mt-8 flex flex-col">
            <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 min-w-full">
              <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-slate-100">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="w-[20%] py-3 pl-6 pr-3 text-left text-xs font-semibold text-slate-500"
                        >
                          Employee
                        </th>
                        <th
                          scope="col"
                          className="w-32 px-3 py-3 text-left text-xs font-semibold text-slate-500"
                        >
                          Submitted
                        </th>
                        <th
                          scope="col"
                          className="w-[24%] px-3 py-3 text-left text-xs font-semibold text-slate-500"
                        >
                          Period
                        </th>
                        <th
                          scope="col"
                          className="w-[22%] px-3 py-3 text-left text-xs font-semibold text-slate-500"
                        >
                          Title
                        </th>
                        <th
                          scope="col"
                          className="w-[10%] px-3 py-3 text-left text-xs font-semibold text-slate-500"
                        >
                          Type
                        </th>
                        <th
                          scope="col"
                          className="w-32 relative py-3 pl-3 pr-6"
                        >
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {timeOffHistory?.map((timeOff, index) => (
                        <tr
                          key={index}
                          className="hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => handleRowClick(timeOff)}
                        >
                          <td className="py-3.5 pl-6 pr-3 text-sm">
                            <div className="flex items-center">
                              <AvatarSmall
                                initials={getInitials(
                                  timeOff.store_team_member.first_name,
                                  timeOff.store_team_member.last_name
                                )}
                              />
                              <div className="ml-2">
                                <div className="font-semibold text-slate-900">
                                  {timeOff.store_team_member.first_name}{" "}
                                  {timeOff.store_team_member.last_name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3.5 text-sm text-slate-500">
                            {formatTimestampDateReadable(timeOff.created_at, props.storeTimezone)}
                          </td>
                          <td className="px-3 py-3.5 text-sm text-slate-500">
                            <div>
                              <span>{formatDateReadable(timeOff.date_lower_bound)}</span>
                              {timeOff.date_upper_bound && (
                                <span> &mdash; {formatDateReadable(timeOff.date_upper_bound)}</span>
                              )}
                            </div>
                            {(timeOff.start_qhnum && timeOff.end_qhnum) &&
                              <div className="text-slate-400 text-xs mt-0.5">
                                {get_t_str(timeOff.start_qhnum)} &mdash; {get_t_str(timeOff.end_qhnum)}
                              </div>
                            }
                          </td>
                          <td className="py-3.5 px-3 text-sm" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                            <div className="font-semibold text-slate-900">
                              {timeOff.title}
                            </div>
                            {timeOff.note && (
                              <div className="text-xs text-slate-400 mt-0.5">{timeOff.note}</div>
                            )}
                          </td>
                          <td className="px-3 py-3.5 text-sm text-slate-500">
                            {timeOff.time_off_type ? (
                              timeOff.time_off_type === 'UNPAID' ? 'Unpaid' :
                              timeOff.time_off_type === 'PTO' ? 'PTO' :
                              timeOff.time_off_type === 'SICK' ? 'Sick' :
                              'Not specified'
                            ) : (
                              <span className="text-slate-400">&mdash;</span>
                            )}
                          </td>
                          <td className="w-32 relative py-3.5 pl-3 pr-6 text-right text-sm font-medium">
                            <div className="flex items-center gap-x-3 justify-end">
                              {timeOff.acting_manager_note && (
                                <Tooltip title={timeOff.acting_manager_note}>
                                  <span className="text-violet-600 cursor-pointer">
                                    <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
                                  </span>
                                </Tooltip>
                              )}
                              <Pill status={timeOff.status.toLowerCase()} />
                              <Tooltip title="Archive">
                                <IconButton
                                  className="text-slate-400"
                                  sx={{ backgroundColor: "#f8fafc", "&:hover": { backgroundColor: "#f1f5f9" } }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    props.archive_time_off_request(timeOff.uuid);
                                  }}
                                >
                                  <ArchiveOutlinedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="border-b border-slate-100"></div>
                  {props.timeOffHistory?.length === 0 && (
                    <div className="w-full mt-16 mb-8 flex items-center justify-center">
                      <div className="w-full flex flex-col items-center justify-center">
                        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                          <InfoOutlinedIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
                        </div>
                        <p className="mt-3 text-sm text-slate-400">
                          No time off history at the moment
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <TimeOffApprovedCalendarModal
        businessWeekStartDayIndex={props.businessWeekStartDayIndex}
        open={open}
        handleClose={handleClose}
        timeOffFutureApproved={props.timeOffFutureApproved}
        blackoutDates={props.blackoutDates}
        create_blackout_date={props.create_blackout_date}
        delete_blackout_date={props.delete_blackout_date}
        respond_to_time_off_request={props.respond_to_time_off_request}
        storeTimezone={props.storeTimezone}
      />
      {selectedRequest && (
        <TimeOffApprovedDrawer
          isDrawerOpen={isDrawerOpen}
          setDrawerOpen={setDrawerOpen}
          requestInfo={selectedRequest}
          storeTimezone={props.storeTimezone}
        />
      )}
    </>
  );
}
